import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MessageSquare, X, Send, Loader2, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CHAT_REQUEST_TIMEOUT_MS = 60_000; // 1 min — Gemini may take a bit on long replies

function getSessionId() {
  let id = localStorage.getItem("strombaron_chat_session");
  if (!id) {
    id = `sess-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem("strombaron_chat_session", id);
  }
  return id;
}

export default function ChatbotWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { id: "welcome", role: "assistant", content: "Hallo! Ich bin dein Strombaron-Berater. Ich helfe dir bei Fragen rund um Strom- und Gastarife – für Privat und Gewerbe, deutschlandweit. Angebote erstelle ich nicht selbst – lade einfach deine Strom- oder Gasrechnung hoch, dann erhältst du innerhalb von 24 Stunden ein individuelles Angebot." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const newMsgId = () => `m-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

  const send = async () => {
    if (!input.trim() || loading) return;
    const userMsg = { id: newMsgId(), role: "user", content: input.trim() };
    setMessages((m) => [...m, userMsg]);
    setInput("");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/chat`, {
        session_id: getSessionId(),
        message: userMsg.content,
      }, { timeout: CHAT_REQUEST_TIMEOUT_MS });
      setMessages((m) => [...m, { id: newMsgId(), role: "assistant", content: res.data.reply }]);
    } catch (err) {
      setMessages((m) => [...m, { id: newMsgId(), role: "assistant", content: "Entschuldigung, es gab ein Problem. Bitte versuchen Sie es erneut oder kontaktieren Sie uns per WhatsApp." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-24 right-6 z-[10000] group inline-flex items-center gap-2 pl-2 pr-4 py-2 rounded-full bg-brand-inverse text-brand-onInverse shadow-[0_12px_32px_-8px_rgba(0,0,0,0.45)] hover:bg-black hover:scale-[1.03] transition-all"
          aria-label="Chatbot öffnen"
          data-testid="chatbot-toggle"
        >
          <span className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: 'var(--brand-green-neon)' }}>
            <Sparkles className="w-4 h-4" style={{ color: '#0A0A0A' }} strokeWidth={2.5} />
          </span>
          <span className="text-sm font-semibold tracking-tight">KI-Berater</span>
          <span className="absolute top-1 right-1 w-2.5 h-2.5 rounded-full bg-brand-energy border-2 border-brand-inverse animate-pulse" />
        </button>
      )}

      {open && (
        <div className="fixed bottom-24 right-6 z-[10000] w-[calc(100vw-3rem)] sm:w-[380px] h-[32rem] max-h-[calc(100vh-8rem)] bg-brand-surface rounded-3xl shadow-[0_24px_60px_-12px_rgba(0,0,0,0.45)] border border-brand-line flex flex-col overflow-hidden" data-testid="chatbot-window">
          <div className="bg-brand-inverse text-brand-onInverse p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: 'var(--brand-green-neon)' }}>
                <Sparkles className="w-4 h-4" style={{ color: '#0A0A0A' }} strokeWidth={2.5} />
              </div>
              <div>
                <p className="font-heading text-sm font-black tracking-tight">Strombaron KI-Assistent</p>
                <p className="text-[11px] text-neutral-400 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-energy animate-pulse" />
                  Online · Antwortet sofort
                </p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="p-1.5 rounded-lg hover:bg-white/10 transition" data-testid="chatbot-close" aria-label="Schließen">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-brand-paper">
            {messages.map((m) => (
              <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                  m.role === "user"
                    ? "bg-brand-inverse text-brand-onInverse rounded-br-md"
                    : "bg-brand-surface border border-brand-line text-brand-ink rounded-bl-md shadow-sm"
                }`} data-testid={`chat-msg-${m.role}-${m.id}`}>
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-brand-surface border border-brand-line rounded-2xl px-4 py-3 text-sm rounded-bl-md shadow-sm flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-ink animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-ink animate-bounce" style={{ animationDelay: '120ms' }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-ink animate-bounce" style={{ animationDelay: '240ms' }} />
                </div>
              </div>
            )}
          </div>

          <div className="p-3 border-t border-brand-line bg-brand-surface">
            <div className="flex items-end gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Frag mich alles zu Strom & Gas…"
                rows={1}
                className="flex-1 resize-none rounded-2xl border border-brand-line bg-brand-surface text-brand-ink px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-ink/20 focus:border-brand-ink placeholder:text-brand-muted/60"
                data-testid="chat-input"
              />
              <button
                onClick={send}
                disabled={loading || !input.trim()}
                className="btn-primary w-10 h-10 rounded-full flex items-center justify-center disabled:opacity-50"
                data-testid="chat-send"
                aria-label="Senden"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
